import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Badge, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuthContext } from '../../providers/AuthProvider.js';
import {
  useOperator,
  useUpdateOperator,
  useDeleteOperator
} from '../../features/operators/hooks/useOperators.js';
import { useOperatorSummary } from '../../features/production-metrics/hooks/useProductionMetrics.js';
import { SHIFT_COLORS, SKILLS } from '../../data/operatorConstants.js';
import OperatorFormModal from '../../components/operators/OperatorFormModal.js';
import DeleteOperatorModal from '../../components/operators/DeleteOperatorModal.js';

const skillLabelMap = Object.fromEntries(SKILLS.map(s => [s.key, s.label]));

const fmtKm = m => {
  if (m == null) return '-';
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
};

const fmtMin = sec => {
  if (sec == null) return '-';
  const m = Math.round(sec / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
};

const OperatorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [authState] = useAuthContext();
  const isAdmin = authState.user?.role === 'admin';

  const { data, isLoading } = useOperator(id);
  const updateMutation = useUpdateOperator();
  const deleteMutation = useDeleteOperator();

  const operator = data?.data;

  // Fetch performance summary filtered by this operator's name
  const { data: perfData } = useOperatorSummary(
    operator?.name ? { operator: operator.name } : {}
  );
  const perfSummary = perfData?.data?.[0];

  const [formModal, setFormModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const handleUpdate = async payload => {
    await updateMutation.mutateAsync(payload);
    setFormModal(false);
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(id);
    navigate('/apps/operators/operator-list');
  };

  if (isLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-6">
        <Spinner animation="border" variant="primary" size="sm" className="me-2" />
        <span className="text-700">Loading...</span>
      </div>
    );
  }

  if (!operator) {
    return (
      <div className="text-center py-6">
        <h4 className="text-700">Operator not found</h4>
        <Button
          variant="link"
          onClick={() => navigate('/apps/operators/operator-list')}
        >
          Back to list
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-9 mt-0">
      {/* Back button */}
      <Button
        variant="link"
        className="p-0 mb-3 text-decoration-none"
        onClick={() => navigate('/apps/operators/operator-list')}
      >
        <FontAwesomeIcon icon="arrow-left" className="me-2" />
        Back to operators
      </Button>

      {/* Header */}
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="d-flex align-items-center gap-2 mb-2">
                <h2 className="mb-0">{operator.name}</h2>
                <Badge bg={SHIFT_COLORS[operator.shift]}>
                  Shift {operator.shift}
                </Badge>
                {!operator.active && (
                  <Badge bg="secondary">Inactive</Badge>
                )}
              </div>
              {operator.notes && (
                <p className="text-700 mb-0">{operator.notes}</p>
              )}
            </div>
            {isAdmin && (
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setFormModal(true)}
                >
                  <FontAwesomeIcon icon="pen" className="me-1" />
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setDeleteModal(true)}
                >
                  <FontAwesomeIcon icon="trash" className="me-1" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        {/* Skills */}
        <Col xs={12} md={6}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">
                <FontAwesomeIcon icon="tools" className="me-2 text-info" />
                Skills
              </h6>
            </Card.Header>
            <Card.Body>
              {operator.skills?.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {operator.skills.map(skill => (
                    <Badge
                      key={skill}
                      bg="info"
                      style={{ fontSize: '0.9rem', fontWeight: 'normal' }}
                    >
                      {skillLabelMap[skill] || skill}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-700 mb-0">No skills added</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Preferred Machines */}
        <Col xs={12} md={6}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <h6 className="mb-0">
                <FontAwesomeIcon icon="cog" className="me-2 text-primary" />
                Preferred Machines
              </h6>
            </Card.Header>
            <Card.Body>
              {operator.preferredMachines?.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {operator.preferredMachines.map(machine => (
                    <Badge
                      key={machine}
                      bg="primary"
                      style={{ fontSize: '0.9rem', fontWeight: 'normal' }}
                    >
                      {machine}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-700 mb-0">No preferred machines</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Performance summary */}
      {perfSummary && (
        <Card className="mb-4">
          <Card.Header className="py-2">
            <h6 className="mb-0">
              <FontAwesomeIcon icon="chart-bar" className="me-2 text-success" />
              Performance Summary
            </h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col xs={6} md={3}>
                <div>
                  <p className="text-700 fs-10 mb-1">Total Production</p>
                  <h5 className="mb-0 text-primary">
                    {fmtKm(perfSummary.totalProducedLength)}
                  </h5>
                  <span className="text-700 fs-10">
                    {perfSummary.totalTokens} drums
                  </span>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div>
                  <p className="text-700 fs-10 mb-1">Avg Speed</p>
                  <h5 className="mb-0 text-info">
                    {perfSummary.avgSpeed
                      ? `${Number(perfSummary.avgSpeed).toFixed(1)} m/min`
                      : '-'}
                  </h5>
                  <span className="text-700 fs-10">while running</span>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div>
                  {/* running = line moving; idle = threading, setup, stoppages */}
                  <p className="text-700 fs-10 mb-1">Running Time</p>
                  <h5 className="mb-0 text-warning">
                    {fmtMin(perfSummary.totalRunningSec)}
                  </h5>
                  <span className="text-700 fs-10">
                    {fmtMin(perfSummary.totalIdleSec)} idle
                  </span>
                </div>
              </Col>
              <Col xs={6} md={3}>
                <div>
                  <p className="text-700 fs-10 mb-1">Machines Used</p>
                  <h5 className="mb-0 text-success">
                    {perfSummary.machines?.length || 0}
                  </h5>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <OperatorFormModal
        show={formModal}
        onHide={() => setFormModal(false)}
        operator={operator}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />

      <DeleteOperatorModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        operator={operator}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

export default OperatorDetail;
